import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makePageT } from '../../shared/utils/pageI18n';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Search, Plus, RefreshCcw, TrendingUp, TrendingDown, Filter, Eye, X, XCircle, LayoutGrid, List, LayoutList, Upload, Download, FileSpreadsheet, FileText, ChevronDown, ChevronUp, CheckCircle, DollarSign, Package, Users, ShoppingCart, AlertCircle, Shield, Layers, Clock, Target } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import AchatsFormModalTabs from './components/AchatsFormModalTabs';
import { buildAchatsColumnDefs, achatsColHeaderProps, ACHATS_FORM_MODAL_SHELL, ACHATS_FORM_TAB_PANEL, ACHATS_FORM_GRID, useAchatsModalTheme, useAchatsColumnVisibility, achatsTableColSpan, ACHATS_TABLE_ACTIONS_TH_CLASS, useAchatsRowSelection, buildAchatsBulkDeleteFilterAction, AchatsBulkDeleteBar, useAppNotificationToast, AchatsCrudNotificationToast, sortRecordsNewestFirst, notifyCreated, notifyUpdated, notifyDeleted, notifyBulkDeleted, notifyError, useAchatsListViewMode, useAchatsListDisplay } from './achatsListPageUtils';
import { useAchatsConfig } from './useAchatsConfig';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import '../../shared/components/custom/CustomComponents.css';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

const NS = 'modules.achats.pages.planificationAchatsPage';

const PlanificationAchatsPage = () => {
  const { t, i18n } = useTranslation();
  const p = makePageT(t, 'modules.achats.pages.planificationAchatsPage');
  const entityLabel = p('entity_label', { defaultValue: p('title') });
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const { cfgTheme, modal: m } = useAchatsModalTheme();
  const achatsConfig = useAchatsConfig();
  const dateLocale = getBrandLocale(i18n.language);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString(dateLocale) : '-';
  const [searchTerm, setSearchTerm] = useState('');
  const [plans, setPlans] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [viewMode, setViewMode] = useAchatsListViewMode(achatsConfig);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingPlan, setViewingPlan] = useState(null);
  
  // Collapsible sections state
  const [formTab, setFormTab] = useState('general');
  const formTabs = useMemo(() => ([
    { id: 'general', label: p('section_basic'), icon: FileText, iconColor: 'text-blue-400' },
    { id: 'details', label: p('section_items_suppliers'), icon: Package, iconColor: 'text-green-400' },
  ]), [p]);
  const [formData, setFormData] = useState({ 
    plan_name: '', 
    plan_type: 'monthly',
    status: 'draft',
    period_start: new Date().toISOString().split('T')[0], 
    period_end: '', 
    total_budget: '', 
    approved_budget: '',
    spent_amount: '',
    category: '',
    priority: 'medium',
    department: '',
    responsible_person: '',
    supplier_count: '',
    item_count: '',
    approval_status: 'pending',
    description: '',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_plans');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  useEffect(() => { loadPlans(); }, []);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadPlans = async () => {
    try {
      const response = await localApiAdapter.get('/achats/planification');
      setPlans(sortRecordsNewestFirst(response?.data || response || []));
    } catch (error) {
      console.error('Error loading plans:', error);
      setPlans([]);
    }
  };

  const stats = {
    total: plans.length,
    active: plans.filter(p => p.status === 'active').length,
    draft: plans.filter(p => p.status === 'draft').length,
    completed: plans.filter(p => p.status === 'completed').length,
    totalBudget: plans.reduce((sum, p) => sum + (parseFloat(p.total_budget) || 0), 0),
    spentAmount: plans.reduce((sum, p) => sum + (parseFloat(p.spent_amount) || 0), 0)
  };

  const mainTabs = useMemo(() => ([
    { id: 'all_plans', name: t('common.general_view'), icon: LayoutGrid },
    { id: 'par_type', name: t('common.by_type'), icon: Shield },
    { id: 'par_statut', name: t('common.by_status'), icon: Target }
  ]), [t]);

  const quickFilterButtons = useMemo(() => ([
    { id: 'all', name: t('common.all'), icon: Layers },
    { id: 'active', name: p('quick_active'), icon: Clock },
    { id: 'draft', name: p('dist_draft'), icon: FileText },
    { id: 'completed', name: p('quick_completed'), icon: CheckCircle }
  ]), [t, p]);

  const planDistribution = useMemo(() => ({
    all_plans: {
      title: p('dist_overview'),
      data: [
        { filterKey: 'active', name: p('dist_active'), count: stats.active, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'draft', name: p('dist_draft'), count: stats.draft, color: 'bg-gradient-to-r from-gray-500 to-gray-600' },
        { filterKey: 'completed', name: p('dist_completed'), count: stats.completed, color: 'bg-gradient-to-r from-blue-500 to-blue-600' }
      ]
    },
    par_type: {
      title: p('dist_by_type'),
      data: [
        { filterKey: 'monthly', name: p('type_monthly'), count: plans.filter(pl => pl.plan_type === 'monthly').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'quarterly', name: p('type_quarterly'), count: plans.filter(pl => pl.plan_type === 'quarterly').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'annual', name: p('type_annual'), count: plans.filter(pl => pl.plan_type === 'annual').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterKey: 'project', name: p('type_project'), count: plans.filter(pl => pl.plan_type === 'project').length, color: 'bg-gradient-to-r from-orange-500 to-orange-600' }
      ]
    },
    par_statut: {
      title: p('dist_by_status'),
      data: [
        { filterKey: 'active', name: p('dist_active'), count: stats.active, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'draft', name: p('dist_preparation'), count: stats.draft, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { filterKey: 'completed', name: p('dist_completed'), count: stats.completed, color: 'bg-gradient-to-r from-blue-500 to-blue-600' }
      ]
    }
  }), [plans, stats, p]);

  const distributionInfo = planDistribution[mainViewMode] || planDistribution.all_plans;

  const baseFilteredPlans = plans.filter(p => {
    // Date range filter
    if (dateRange.start || dateRange.end) {
      const planDate = new Date(p.period_start);
      if (dateRange.start) {
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (planDate < startOfDay) return false;
      }
      if (dateRange.end) {
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (planDate > endOfDay) return false;
      }
    }
    
    // Search filter
    if (searchTerm && !p.plan_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    // Quick filter (only for Vue Générale)
    if (mainViewMode === 'all_plans') {
      if (quickFilter === 'active' && p.status !== 'active') return false;
      if (quickFilter === 'draft' && p.status !== 'draft') return false;
      if (quickFilter === 'completed' && p.status !== 'completed') return false;
    }
    
    // Distribution card filter (clickable stats)
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_plans':
          if (activeDistributionFilter === 'active' && p.status !== 'active') return false;
          if (activeDistributionFilter === 'draft' && p.status !== 'draft') return false;
          if (activeDistributionFilter === 'completed' && p.status !== 'completed') return false;
          break;
        case 'par_type':
          if (activeDistributionFilter === 'monthly' && p.plan_type !== 'monthly') return false;
          if (activeDistributionFilter === 'quarterly' && p.plan_type !== 'quarterly') return false;
          if (activeDistributionFilter === 'annual' && p.plan_type !== 'annual') return false;
          if (activeDistributionFilter === 'project' && p.plan_type !== 'project') return false;
          break;
        case 'par_statut':
          if (activeDistributionFilter === 'active' && p.status !== 'active') return false;
          if (activeDistributionFilter === 'draft' && p.status !== 'draft') return false;
          if (activeDistributionFilter === 'completed' && p.status !== 'completed') return false;
          break;
      }
    }
    
    return true;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { 
        plan_name: formData.plan_name,
        plan_type: formData.plan_type,
        status: formData.status,
        period_start: formData.period_start,
        period_end: formData.period_end,
        total_budget: parseFloat(formData.total_budget) || 0,
        approved_budget: parseFloat(formData.approved_budget) || 0,
        spent_amount: parseFloat(formData.spent_amount) || 0,
        category: formData.category,
        priority: formData.priority,
        department: formData.department,
        responsible_person: formData.responsible_person,
        supplier_count: parseInt(formData.supplier_count) || 0,
        item_count: parseInt(formData.item_count) || 0,
        approval_status: formData.approval_status,
        description: formData.description,
        notes: formData.notes
      };
      if (editingPlan) {
        await localApiAdapter.put(`/achats/planification/${editingPlan.id}`, submitData);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/achats/planification', submitData);
        notifyCreated(showNotify, entityLabel);
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('alert_save_error', { message: error.message || p('empty_dash') }));
    } finally {
      resetForm();
      loadPlans();
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({ 
      plan_name: plan.plan_name || '',
      plan_type: plan.plan_type || 'monthly',
      status: plan.status || 'draft',
      period_start: plan.period_start || '',
      period_end: plan.period_end || '',
      total_budget: plan.total_budget?.toString() || '',
      approved_budget: plan.approved_budget?.toString() || '',
      spent_amount: plan.spent_amount?.toString() || '',
      category: plan.category || '',
      priority: plan.priority || 'medium',
      department: plan.department || '',
      responsible_person: plan.responsible_person || '',
      supplier_count: plan.supplier_count?.toString() || '',
      item_count: plan.item_count?.toString() || '',
      approval_status: plan.approval_status || 'pending',
      description: plan.description || '',
      notes: plan.notes || ''
    });
    setFormTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/achats/planification/${id}`);
        notifyDeleted(showNotify, entityLabel);
        loadPlans();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_save_error', { message: p('empty_dash') }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedPlans.length }))) {
      try {
        const deleteCount = selectedPlans.length;
        await Promise.all(selectedPlans.map(id => localApiAdapter.delete(`/achats/planification/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, entityLabel);
        setSelectedPlans([]);
        loadPlans();
      } catch (error) {
        console.error('Error deleting plans:', error);
        notifyError(showNotify, p('alert_save_error', { message: p('empty_dash') }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      plan_name: '', 
      plan_type: 'monthly',
      status: 'draft',
      period_start: '', 
      period_end: '', 
      total_budget: '', 
      approved_budget: '',
      spent_amount: '',
      category: '',
      priority: 'medium',
      department: '',
      responsible_person: '',
      supplier_count: '',
      item_count: '',
      approval_status: 'pending',
      description: '',
      notes: ''
    });
    setEditingPlan(null);
    setFormTab('general');
    setShowModal(false);
  };

  const handleExport = () => {
    const headers = [
      { key: 'plan_name', label: p('export_col_plan_name') },
      { key: 'plan_type', label: p('export_col_type') },
      { key: 'status', label: p('export_col_status') },
      { key: 'period_start', label: p('export_col_start') },
      { key: 'period_end', label: p('export_col_end') },
      { key: 'total_budget', label: p('export_col_budget') },
      { key: 'spent_amount', label: p('export_col_spent') },
      { key: 'priority', label: p('export_col_priority') }
    ];
    const fileName = `planification_achats_${new Date().toISOString().split('T')[0]}`;
    exportToExcel(filteredPlans, headers, `${fileName}.xlsx`);
  };

  const handleImport = () => {
    const headers = [
      { key: 'plan_name', label: p('export_col_plan_name') },
      { key: 'plan_type', label: p('export_col_type') },
      { key: 'status', label: p('export_col_status') },
      { key: 'period_start', label: p('export_col_start') },
      { key: 'total_budget', label: p('export_col_budget') }
    ];
    const schema = {
      requiredFields: ['plan_name', 'plan_type'],
      validators: {
        total_budget: (value) => !value || !isNaN(parseFloat(value))
      }
    };
    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        if (result.success) {
          let successCount = 0;
          for (const record of result.data) {
            try {
              await localApiAdapter.post('/achats/planification', record);
              successCount++;
            } catch (err) {
              console.error('Import record error:', err);
            }
          }
          await loadPlans();
          alert(p('alert_import_success', { total: result.totalCount, imported: successCount }));
        }
      } catch (error) {
        console.error('Import error:', error);
        alert(p('alert_import_error'));
      }
    }, '.csv,.xlsx,.xls');
  };


  const handleSelectAll = () => {
    if (rowSelection.allRowsSelected) {
      setSelectedPlans([]);
    } else {
      setSelectedPlans(filteredPlans.map((r) => r.id));
    }
  };
  
  const handleSelectPlan = (id) => { 
    setSelectedPlans(selectedPlans.includes(id) ? selectedPlans.filter(pId => pId !== id) : [...selectedPlans, id]); 
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/achats/planification/${recordId}`, { status: newStatus });
      await loadPlans();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getStatusBadge = (status) => {
    const config = statusOptions.find((s) => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style } = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}>{Icon ? <Icon size={14} /> : null}{label}</span>;
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      low: { label: p('priority_low'), style: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
      medium: { label: p('priority_medium'), style: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
      high: { label: p('priority_high'), style: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
      critical: { label: p('priority_critical'), style: 'bg-red-500/20 text-red-300 border-red-500/30' }
    };
    const config = priorityConfig[priority] || priorityConfig.medium;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${config.style}`}>{config.label}</span>;
  };

  const getApprovalBadge = (approval) => {
    const approvalConfig = {
      pending: { label: p('approval_pending'), style: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
      approved: { label: p('approval_approved'), style: 'bg-green-500/20 text-green-300 border-green-500/30' },
      rejected: { label: p('approval_rejected'), style: 'bg-red-500/20 text-red-300 border-red-500/30' }
    };
    const config = approvalConfig[approval] || approvalConfig.pending;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${config.style}`}>{config.label}</span>;
  };

  const planTypeOptions = useMemo(() => ([
    { value: 'monthly', label: p('type_monthly') },
    { value: 'quarterly', label: p('type_quarterly') },
    { value: 'annual', label: p('type_annual') },
    { value: 'project', label: p('type_project_specific') }
  ]), [p]);

  const statusOptions = useMemo(() => ([
    { value: 'draft', label: p('status_draft'), icon: FileText, style: 'bg-gray-500/20 text-gray-300 border-gray-500/30' },
    { value: 'active', label: p('status_active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'completed', label: p('status_completed'), icon: Shield, style: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
    { value: 'cancelled', label: p('status_cancelled'), icon: XCircle, style: 'bg-red-500/20 text-red-300 border-red-500/30' }
  ]), [p]);

  const priorityOptions = useMemo(() => ([
    { value: 'low', label: p('priority_low') },
    { value: 'medium', label: p('priority_medium') },
    { value: 'high', label: p('priority_high') },
    { value: 'critical', label: p('priority_critical') }
  ]), [p]);

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('plan_name'),
      exactColumnRule('plan_type', (row) => row.plan_type || ''),
      textColumnRule('period_start', (row) => (row.period_start ? String(row.period_start).split('T')[0] : '')),
      textColumnRule('total_budget', (row) => String(row.total_budget ?? '')),
      textColumnRule('spent_amount', (row) => String(row.spent_amount ?? '')),
      exactColumnRule('priority', (row) => row.priority || ''),
      exactColumnRule('status', (row) => row.status || ''),
    ],
    [],
  );

  const { filtered: filteredPlans, displayed: displayedPlans } = useAchatsListDisplay(
    baseFilteredPlans,
    colFilters,
    columnFilterRules,
    achatsConfig.itemsPerPage,
  );

  const rowSelection = useAchatsRowSelection(selectedPlans, filteredPlans);

  const colHeader = (columnKey, label, opts = {}) =>
    achatsColHeaderProps({ columnKey, label, colFilters, setColFilter, editingKey, setEditingKey, ...opts });

  const approvalOptions = useMemo(() => ([
    { value: 'pending', label: p('approval_pending') },
    { value: 'approved', label: p('approval_approved') },
    { value: 'rejected', label: p('approval_rejected') }
  ]), [p]);

  const categoryOptions = useMemo(() => ([
    { value: 'matières_premières', label: p('category_raw_materials') },
    { value: 'services', label: p('category_services') },
    { value: 'equipements', label: p('category_equipment') },
    { value: 'fournitures', label: p('category_supplies') },
    { value: 'transport', label: p('category_transport') }
  ]), [p]);

  const kpiStats = useMemo(() => ([
    { title: p('stat_total'), value: stats.total, icon: Calendar, color: 'from-blue-500 to-blue-600' },
    { title: p('stat_active'), value: stats.active, icon: Clock, color: 'from-green-500 to-green-600' },
    { title: p('dist_draft'), value: stats.draft, icon: FileText, color: 'from-gray-500 to-gray-600' },
    { title: p('stat_completed'), value: stats.completed, icon: CheckCircle, color: 'from-purple-500 to-purple-600' },
    { title: p('stat_total_budget'), value: formatBrandMoney(stats.totalBudget), icon: DollarSign, color: 'from-amber-500 to-amber-600' },
    { title: p('stat_total_spent'), value: formatBrandMoney(stats.spentAmount), icon: TrendingDown, color: 'from-red-500 to-red-600' }
  ]), [stats, p]);

  const planificationColumnKeys = useMemo(
    () => ['plan_name', 'plan_type', 'period_start', 'total_budget', 'spent_amount', 'priority', 'status', 'actions'],
    [],
  );
  const { columnVisibility, handleToggleColumn, isColumnVisible } = useAchatsColumnVisibility(planificationColumnKeys);
  const qawaemColumns = useMemo(
    () => buildAchatsColumnDefs([
      { key: 'plan_name', labelKey: 'col_plan_name' },
      { key: 'plan_type', labelKey: 'label_type' },
      { key: 'period_start', labelKey: 'label_period' },
      { key: 'total_budget', labelKey: 'col_budget' },
      { key: 'spent_amount', labelKey: 'col_spent' },
      { key: 'priority', labelKey: 'label_priority' },
      { key: 'status', labelKey: 'label_status' },
      { key: 'actions', label: t('common.actions'), required: true },
    ], p, columnVisibility),
    [p, t, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const tableColSpan = achatsTableColSpan(qawaemColumns);

  return (
    <div className={`space-y-6 employees-container`}>
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Calendar className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button onClick={loadPlans} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} 
            className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{t('common.advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button 
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2" 
            onClick={() => { setEditingPlan(null); setFormTab('general');
    setShowModal(true); }}
          >
            <Plus className="h-5 w-5" />
            <span>{p('new_item')}</span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiStats.slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white force-white" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Advanced Options */}
      <motion.div 
        initial={false} 
        animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} 
        transition={{ duration: 0.3, ease: "easeInOut" }} 
        style={{ overflow: "hidden" }}
      >
        <div className="space-y-6 pt-6">
          {/* Main Tabs */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button 
                  key={tab.id} 
                  onClick={() => { 
                    setMainViewMode(tab.id); 
                    setQuickFilter('all'); 
                    setActiveDistributionFilter(null); 
                  }} 
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    mainViewMode === tab.id 
                      ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' 
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Quick Filter Buttons (only for Vue Générale) */}
          {mainViewMode === 'all_plans' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button 
                    key={filter.id} 
                    onClick={() => setQuickFilter(filter.id)} 
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                      quickFilter === filter.id 
                        ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' 
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <filter.icon className="h-5 w-5" />
                    <span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Distribution Cards */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.1 }} 
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button 
                  onClick={() => setActiveDistributionFilter(null)}
                  className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                >
                  <XCircle size={14} />
                  {p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3', 'lg:grid-cols-4')}>{distributionInfo.data.map((item) => (
                <button
                  key={item.filterKey}
                  onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterKey ? null : item.filterKey)}
                  className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${
                    activeDistributionFilter === item.filterKey 
                      ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' 
                      : 'border-white/10 hover:border-white/20 hover:bg-white/10'
                  }`}
                  title={p('filter_click_title', { name: item.name })}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.filterKey ? 'text-white' : 'text-white/80'}`}>
                      {item.name}
                    </h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>
                      {item.count}
                    </div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div 
                      className={`h-3 rounded-full ${item.color} transition-all`} 
                      style={{ 
                        width: `${(() => {
                          const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                          return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                        })()}%` 
                      }}
                    ></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.filterKey ? 'text-white/80' : 'text-white/60'}`}>
                    {(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      const pct = total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                      return p('pct_of_plans', { pct });
                    })()}
                  </p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Table Container */}
      <div className="glass-panel glass-table-shell overflow-visible">
        <AchatsBulkDeleteBar
          selectedCount={selectedPlans.length}
          onDelete={handleBulkDelete}
          deleteLabel={`${t('common.delete')} (${selectedPlans.length})`}
        />

        {/* Table View */}
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={rowSelection.allRowsSelected}
                        indeterminate={rowSelection.someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  
                  {isColumnVisible('plan_name') ? <TableColumnHeaderFilter {...colHeader('plan_name', p('col_plan_name'))} /> : null}
                  {isColumnVisible('plan_type') ? <TableColumnHeaderFilter {...colHeader('plan_type', p('label_type'), { kind: 'select', options: planTypeOptions })} /> : null}
                  {isColumnVisible('period_start') ? <TableColumnHeaderFilter {...colHeader('period_start', p('label_period'))} /> : null}
                  {isColumnVisible('total_budget') ? <TableColumnHeaderFilter {...colHeader('total_budget', p('col_budget'), { align: 'center' })} /> : null}
                  {isColumnVisible('spent_amount') ? <TableColumnHeaderFilter {...colHeader('spent_amount', p('col_spent'), { align: 'center' })} /> : null}
                  {isColumnVisible('priority') ? <TableColumnHeaderFilter {...colHeader('priority', p('label_priority'), { kind: 'select', options: priorityOptions, align: 'center' })} /> : null}
                  {isColumnVisible('status') ? <TableColumnHeaderFilter {...colHeader('status', p('label_status'), { kind: 'select', options: statusOptions, align: 'center' })} /> : null}
                  <QawaemTableHeaderActionsCell
                    thClassName={ACHATS_TABLE_ACTIONS_TH_CLASS}
                    label={t('common.actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumns.length}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {displayedPlans.length > 0 ? (
                  displayedPlans.map((plan, index) => (
                    <tr 
                      key={plan.id} 
                      className={`hover:bg-white/5 transition-colors ${selectedPlans.includes(plan.id) ? 'bg-white/10' : ''}`}
                    >
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                          <div className="flex justify-center">
                            <QawaemTableCheckbox
                              checked={selectedPlans.includes(plan.id)}
                              onChange={() => handleSelectPlan(plan.id)}
                            />
                          </div>
                        </td>
                      {isColumnVisible('plan_name') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{plan.plan_name}</span>
                          <span className="text-white/50 text-xs mt-0.5">{plan.category || p('uncategorized')}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColumnVisible('plan_type') ? (
                      <td className="px-6 py-4">
                        <span className="text-white/80">{planTypeOptions.find(t => t.value === plan.plan_type)?.label || plan.plan_type}</span>
                      </td>
                      ) : null}
                      {isColumnVisible('period_start') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{fmtDate(plan.period_start)}</span>
                          <span className="text-white/50 text-xs mt-0.5">
                            {plan.period_end ? `${p('end_prefix')} ${fmtDate(plan.period_end)}` : p('period_end_none')}
                          </span>
                        </div>
                      </td>
                      ) : null}
                      {isColumnVisible('total_budget') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{formatBrandMoney((plan.total_budget || 0))}</span>
                          <span className="text-white/50 text-xs mt-0.5">{p('approved_prefix')} {formatBrandMoney((plan.approved_budget || 0))}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColumnVisible('spent_amount') ? (
                      <td className="px-6 py-4">
                        <span className="text-red-300 font-semibold">{formatBrandMoney((plan.spent_amount || 0))}</span>
                      </td>
                      ) : null}
                      {isColumnVisible('priority') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">{getPriorityBadge(plan.priority)}</div>
                      </td>
                      ) : null}
                      {isColumnVisible('status') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge status={plan.status} recordId={plan.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'تغيير الحالة' }) })} />
                        </div>
                      </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingPlan(plan); setShowViewModal(true); }}
                          onEdit={() => handleEdit(plan)}
                          onDelete={() => handleDelete(plan.id)}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                      <Calendar size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Card View */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedPlans.length > 0 ? (
              displayedPlans.map((plan, index) => (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Calendar size={20} className="text-blue-400" />
                          {plan.plan_name}
                        </h3>
                        <div className="flex gap-2">
                          <QawaemClickableStatusBadge status={plan.status} recordId={plan.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'تغيير الحالة' }) })} />
                          {getPriorityBadge(plan.priority)}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_type')}:</span> {planTypeOptions.find(t => t.value === plan.plan_type)?.label}</div>
                            <div><span className="font-medium">{p('label_category')}:</span> {plan.category || p('uncategorized')}</div>
                            <div><span className="font-medium">{p('label_responsible')}:</span> {plan.responsible_person || p('na')}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_period')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_start')}:</span> {fmtDate(plan.period_start)}</div>
                            <div><span className="font-medium">{p('label_end')}:</span> {plan.period_end ? fmtDate(plan.period_end) : p('empty_dash')}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_budget')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_total')}:</span> {formatBrandMoney((plan.total_budget || 0))}</div>
                            <div><span className="font-medium">{p('approved_prefix')}</span> {formatBrandMoney((plan.approved_budget || 0))}</div>
                            <div><span className="font-medium">{p('label_spent')}:</span> <span className="text-red-300">{formatBrandMoney((plan.spent_amount || 0))}</span></div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_other')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_items')}:</span> {plan.item_count || 0}</div>
                            <div><span className="font-medium">{p('label_suppliers')}:</span> {plan.supplier_count || 0}</div>
                            <div><span className="font-medium">{p('label_approval')}:</span> {getApprovalBadge(plan.approval_status)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingPlan(plan); setShowViewModal(true); }}
                      onEdit={() => handleEdit(plan)}
                      onDelete={() => handleDelete(plan.id)}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Calendar size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* View Modal */}
      {showViewModal && viewingPlan ? (
      <QawaemErpViewModal
        open
        onClose={() => setShowViewModal(false)}
        title={p('view_title')}
        icon={Eye}
        testId="achats-view-modal"
      >
        <div className="space-y-4">
                <div className={m.viewSection}>
                  <h3 className={m.viewSectionTitle}>{p('section_general')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className={m.viewLabel}>{p('label_name')}</p>
                      <p className={m.viewValue}>{viewingPlan.plan_name}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('label_type')}</p>
                      <p className={m.viewValue}>{planTypeOptions.find(t => t.value === viewingPlan.plan_type)?.label}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('label_status')}</p>
                      <div className="flex">{getStatusBadge(viewingPlan.status)}</div>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('label_priority')}</p>
                      <div className="flex">{getPriorityBadge(viewingPlan.priority)}</div>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('label_approval')}</p>
                      <div className="flex">{getApprovalBadge(viewingPlan.approval_status)}</div>
                    </div>
                  </div>
                </div>

                {viewingPlan.description && (
                  <div className={m.viewSection}>
                    <h3 className={`${m.viewSectionTitle} mb-2`}>{p('label_description')}</h3>
                    <p className="text-white/80">{viewingPlan.description}</p>
                  </div>
                )}

                {viewingPlan.notes && (
                  <div className={m.viewSection}>
                    <h3 className={`${m.viewSectionTitle} mb-2`}>{p('label_notes')}</h3>
                    <p className="text-white/80 whitespace-pre-wrap">{viewingPlan.notes}</p>
                  </div>
                )}
        </div>
      </QawaemErpViewModal>
      ) : null}

      {/* Edit/Create Modal - WITH CUSTOM COMPONENTS */}
      <QawaemErpCrudModal
        open={showModal}
        onClose={() => resetForm()}
        title={editingPlan ? p('modal_edit') : p('modal_create')}
        icon={Calendar}
        formId="plan-form"
        saveLabel={t('common.save')}
        onSubmit
        testId="achats-crud-modal"
      >
        <div className={ACHATS_FORM_MODAL_SHELL}>
          <AchatsFormModalTabs
            tabs={formTabs}
            activeTab={formTab}
            onTabChange={setFormTab}
            isLight={m.isLight}
            ariaLabel={editingPlan ? p('modal_edit') : p('modal_create')}
          />
          <div className={ACHATS_FORM_TAB_PANEL}>
            <form id="plan-form" onSubmit={handleSubmit}>
                {formTab === 'general' && (
                  <div className={ACHATS_FORM_GRID}>
                    <div>
                      <label className={cfgTheme.label}><RequiredLabelText text={p('label_plan_name')} required /></label>
                      <input
                        type="text"
                        required
                        value={formData.plan_name}
                        onChange={(e) => setFormData({ ...formData, plan_name: e.target.value })}
                        className={cfgTheme.input}
                        placeholder={p('placeholder_plan_name')}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_plan_type')}</label>
                      <CustomSelect
                        value={formData.plan_type}
                        onChange={(value) => setFormData({ ...formData, plan_type: value || 'monthly' })}
                        options={planTypeOptions}
                        placeholder={p('placeholder_select_or_type')}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_status')}</label>
                      <CustomSelect
                        value={formData.status}
                        onChange={(value) => setFormData({ ...formData, status: value || 'draft' })}
                        options={statusOptions}
                        placeholder={p('placeholder_select_or_type')}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_priority')}</label>
                      <CustomSelect
                        value={formData.priority}
                        onChange={(value) => setFormData({ ...formData, priority: value || 'medium' })}
                        options={priorityOptions}
                        placeholder={p('placeholder_select_or_type')}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_category')}</label>
                      <CustomSelect
                        value={formData.category}
                        onChange={(value) => setFormData({ ...formData, category: value || '' })}
                        options={categoryOptions}
                        placeholder={p('placeholder_select_or_type')}
                        searchable
                        creatable
                        autoSelect={false}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_department')}</label>
                      <input
                        type="text"
                        value={formData.department}
                        onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                        className={cfgTheme.input}
                        placeholder={p('placeholder_department')}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_responsible')}</label>
                      <input
                        type="text"
                        value={formData.responsible_person}
                        onChange={(e) => setFormData({ ...formData, responsible_person: e.target.value })}
                        className={cfgTheme.input}
                        placeholder={p('placeholder_responsible')}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_approval_status')}</label>
                      <CustomSelect
                        value={formData.approval_status}
                        onChange={(value) => setFormData({ ...formData, approval_status: value || 'pending' })}
                        options={approvalOptions}
                        placeholder={p('placeholder_select_or_type')}
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className={cfgTheme.label}>{p('label_description')}</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30 min-h-[100px]"
                        placeholder={p('placeholder_plan_description')}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_period_start')}</label>
                      <CustomDatePicker
                        value={formData.period_start}
                        onChange={(date) => setFormData({ ...formData, period_start: date })}
                        placeholder={p('placeholder_start_date')}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_period_end')}</label>
                      <CustomDatePicker
                        value={formData.period_end}
                        onChange={(date) => setFormData({ ...formData, period_end: date })}
                        placeholder={p('placeholder_end_date')}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_total_budget')}</label>
                      <CustomNumberInput
                        value={formData.total_budget}
                        onChange={(value) => setFormData({ ...formData, total_budget: value })}
                        placeholder="0.00"
                        step={1000}
                        min={0}
                        decimals={2}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_approved_budget')}</label>
                      <CustomNumberInput
                        value={formData.approved_budget}
                        onChange={(value) => setFormData({ ...formData, approved_budget: value })}
                        placeholder="0.00"
                        step={1000}
                        min={0}
                        decimals={2}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_spent_amount')}</label>
                      <CustomNumberInput
                        value={formData.spent_amount}
                        onChange={(value) => setFormData({ ...formData, spent_amount: value })}
                        placeholder="0.00"
                        step={1000}
                        min={0}
                        decimals={2}
                      />
                    </div>
                  </div>
                )}

                {formTab === 'details' && (
                  <div className={ACHATS_FORM_GRID}>
                    <div>
                      <label className={cfgTheme.label}>{p('label_item_count')}</label>
                      <CustomNumberInput
                        value={formData.item_count}
                        onChange={(value) => setFormData({ ...formData, item_count: value })}
                        placeholder="0"
                        step={1}
                        min={0}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_supplier_count')}</label>
                      <CustomNumberInput
                        value={formData.supplier_count}
                        onChange={(value) => setFormData({ ...formData, supplier_count: value })}
                        placeholder="0"
                        step={1}
                        min={0}
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className={cfgTheme.label}>{p('label_notes')}</label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30 min-h-[150px]"
                        placeholder={p('placeholder_notes')}
                      />
                    </div>
                  </div>
                )}

            </form>
          </div>
        </div>
      </QawaemErpCrudModal>
      <AchatsCrudNotificationToast notifyToast={notifyToast} onDismiss={dismissNotify} />
    </div>
  );
};

export default PlanificationAchatsPage;

